import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { listingPath } from "@/lib/constants";
import { toast } from "@/lib/toast";
import Navbar from "@/components/Navbar";
import { LoadFailed } from "@/components/LoadState";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tag, Loader2, ArrowRight, Clock, Check, X, Undo2, Handshake } from "lucide-react";
import { CheckoutModal } from "@/components/CheckoutModal";

const money = (c) => (c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);

const STATUS_META = {
  pending: { label: "In negotiation", cls: "text-volt border-volt/50" },
  accepted: { label: "Accepted", cls: "text-olive-bright border-olive/50" },
  declined: { label: "Declined", cls: "text-muted-foreground border-sniper" },
  withdrawn: { label: "Withdrawn", cls: "text-muted-foreground border-sniper" },
  expired: { label: "Expired", cls: "text-muted-foreground border-sniper" },
  closed_sold: { label: "Sold elsewhere", cls: "text-muted-foreground border-sniper" },
  completed: { label: "Purchased", cls: "text-steel border-steel/50" },
};

function reservedLabel(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return "reservation expired";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
}

function OfferCard({ offer, onAction, onCounter }) {
  const s = STATUS_META[offer.status] || { label: offer.status, cls: "text-muted-foreground border-sniper" };
  const iAmSeller = offer.is_seller;
  const [busy, setBusy] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const act = async (verb) => {
    setBusy(true);
    try { await onAction(offer, verb); } finally { setBusy(false); }
  };

  // Accepted-offer checkout — same method + processing confirm step as Buy Now.
  const checkout = () => setCheckoutOpen(true);

  return (
    <div className="border border-sniper bg-surface" data-testid={`offer-card-${offer.offer_id}`}>
      <div className="flex gap-3 p-3">
        <Link to={listingPath(offer)} className="w-16 h-16 shrink-0 border border-sniper bg-obsidian overflow-hidden">
          {offer.listing_photo
            ? <img src={offer.listing_photo} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full" />}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Link to={listingPath(offer)} className="font-heading text-sm text-foreground hover:text-olive-bright truncate leading-tight">
              {offer.listing_title || "Listing"}
            </Link>
            <span className={`shrink-0 font-mono text-[9px] uppercase border px-1.5 py-0.5 ${s.cls}`} data-testid={`offer-status-${offer.offer_id}`}>{s.label}</span>
          </div>
          {offer.variant_label && (
            <div className="font-mono text-[10px] uppercase text-muted-foreground mt-0.5" data-testid={`offer-variant-${offer.offer_id}`}>
              Option: {offer.variant_label}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 font-mono text-xs">
            <span className="text-muted-foreground">Asking {money(offer.listing_price_cents)}</span>
            <ArrowRight className="w-3 h-3 text-muted-foreground" />
            <span className="text-foreground font-bold tabular-nums" data-testid={`offer-amount-${offer.offer_id}`}>
              {iAmSeller ? "@" + (offer.buyer_username || "buyer") : "You"}: {money(offer.status === "accepted" ? offer.agreed_amount_cents : offer.amount_cents)}
            </span>
          </div>
          {offer.message && <p className="mt-1 font-body text-xs text-secondary-foreground line-clamp-2">"{offer.message}"</p>}
        </div>
      </div>

      {/* action bar */}
      <div className="border-t border-sniper px-3 py-2 flex flex-wrap items-center gap-2">
        {offer.status === "pending" && offer.awaiting_me && (
          <>
            <button onClick={() => act("accept")} disabled={busy} data-testid={`offer-accept-${offer.offer_id}`}
              className="inline-flex items-center gap-1 bg-olive hover:bg-olive-bright text-white font-mono uppercase text-[11px] px-3 py-1.5 transition-colors disabled:opacity-50">
              <Check className="w-3.5 h-3.5" /> Accept
            </button>
            {offer.can_counter && (
              <button onClick={() => onCounter(offer)} disabled={busy} data-testid={`offer-counter-${offer.offer_id}`}
                className="inline-flex items-center gap-1 border border-sniper hover:border-olive text-foreground font-mono uppercase text-[11px] px-3 py-1.5 transition-colors disabled:opacity-50">
                <Handshake className="w-3.5 h-3.5" /> Counter
              </button>
            )}
            <button onClick={() => act("decline")} disabled={busy} data-testid={`offer-decline-${offer.offer_id}`}
              className="inline-flex items-center gap-1 border border-sniper hover:border-blaze/60 text-muted-foreground hover:text-blaze font-mono uppercase text-[11px] px-3 py-1.5 transition-colors disabled:opacity-50">
              <X className="w-3.5 h-3.5" /> Decline
            </button>
          </>
        )}
        {offer.status === "pending" && !offer.awaiting_me && (
          <span className="font-mono text-[11px] uppercase text-muted-foreground flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {iAmSeller ? "Waiting on buyer" : "Waiting on seller"}
          </span>
        )}
        {offer.status === "pending" && offer.is_buyer && (
          <button onClick={() => act("withdraw")} disabled={busy} data-testid={`offer-withdraw-${offer.offer_id}`}
            className="ml-auto inline-flex items-center gap-1 text-muted-foreground hover:text-foreground font-mono uppercase text-[11px] px-2 py-1.5 transition-colors disabled:opacity-50">
            <Undo2 className="w-3.5 h-3.5" /> Withdraw
          </button>
        )}
        {offer.status === "accepted" && offer.is_buyer && (
          <>
            <button onClick={checkout} disabled={busy} data-testid={`offer-checkout-${offer.offer_id}`}
              className="inline-flex items-center gap-1.5 font-heading font-bold uppercase text-sm px-4 py-2 transition-all hover:brightness-105 disabled:opacity-50"
              style={{ backgroundColor: "#cfe86b", color: "#0a0c05" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Complete checkout <ArrowRight className="w-4 h-4" /></>}
            </button>
            <span className="font-mono text-[10px] uppercase text-volt flex items-center gap-1">
              <Clock className="w-3 h-3" /> {reservedLabel(offer.reserved_until)}
            </span>
          </>
        )}
        {offer.status === "accepted" && offer.is_seller && (
          <span className="font-mono text-[11px] uppercase text-olive-bright flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5" /> Reserved for @{offer.buyer_username} — awaiting checkout
          </span>
        )}
      </div>
      {checkoutOpen && (
        <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)}
          listingId={offer.listing_id} title={offer.listing_title} offerId={offer.offer_id} />
      )}
    </div>
  );
}

export default function Offers() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [err, setErr] = useState(false);
  const [counterFor, setCounterFor] = useState(null);
  const [counterAmt, setCounterAmt] = useState("");
  const [counterMsg, setCounterMsg] = useState("");
  const [counterBusy, setCounterBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(false);
    try {
      const [m, i] = await Promise.all([api.get("/offers/mine"), api.get("/offers/incoming")]);
      setMine(m.data.offers || []);
      setIncoming(i.data.offers || []);
    } catch { setErr(true); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doAction = async (offer, verb) => {
    try {
      await api.post(`/offers/${offer.offer_id}/${verb}`, verb === "decline" ? {} : undefined);
      toast.success(verb === "accept" ? "Offer accepted." : verb === "decline" ? "Offer declined." : "Offer withdrawn.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const openCounter = (offer) => { setCounterFor(offer); setCounterAmt(""); setCounterMsg(""); };

  const submitCounter = async () => {
    const amt = parseFloat(counterAmt);
    if (!amt || amt <= 0) { toast.error("Enter a counter amount."); return; }
    setCounterBusy(true);
    try {
      await api.post(`/offers/${counterFor.offer_id}/counter`, { amount: amt, message: counterMsg.trim() || undefined });
      toast.success("Counter sent.");
      setCounterFor(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setCounterBusy(false); }
  };

  const list = tab === "mine" ? mine : incoming;
  const loading = list === null && !err;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl font-black uppercase flex items-center gap-2" data-testid="offers-heading">
          <Tag className="w-7 h-7 text-olive-bright" /> Offers
        </h1>
        <p className="mt-1 font-mono text-xs text-muted-foreground">Haggle on price — accepted offers reserve the listing for 24h.</p>

        <div className="mt-5 flex gap-1 border-b border-sniper">
          <button onClick={() => setTab("mine")} data-testid="offers-tab-mine"
            className={`px-4 py-2 font-mono uppercase text-xs -mb-px border-b-2 transition-colors ${tab === "mine" ? "border-olive-bright text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            My offers{mine?.length ? ` (${mine.length})` : ""}
          </button>
          <button onClick={() => setTab("incoming")} data-testid="offers-tab-incoming"
            className={`px-4 py-2 font-mono uppercase text-xs -mb-px border-b-2 transition-colors ${tab === "incoming" ? "border-olive-bright text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            Incoming{incoming?.length ? ` (${incoming.length})` : ""}
          </button>
        </div>

        {err ? (
          <div className="mt-6"><LoadFailed onRetry={load} /></div>
        ) : loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : list.length === 0 ? (
          <div className="mt-8 border border-sniper bg-surface p-10 text-center" data-testid="offers-empty">
            <p className="font-body text-muted-foreground">
              {tab === "mine"
                ? <>You haven't made any offers yet. <Link to="/feed" className="text-olive-bright">Browse the marketplace</Link> and use "Make Offer" on a listing.</>
                : "No incoming offers on your listings yet."}
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-3" data-testid={`offers-list-${tab}`}>
            {list.map((o) => (
              <OfferCard key={o.offer_id} offer={o} onAction={doAction} onCounter={openCounter} />
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!counterFor} onOpenChange={(v) => !v && setCounterFor(null)}>
        <DialogContent className="bg-surface border border-sniper max-w-md" data-testid="counter-modal">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-foreground">Counter offer</DialogTitle>
            <DialogDescription className="font-mono text-xs text-muted-foreground">{counterFor?.listing_title}</DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Your counter ($)</span>
            <input type="number" min="0" step="1" autoFocus value={counterAmt} onChange={(e) => setCounterAmt(e.target.value)}
              data-testid="counter-amount-input"
              className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-heading text-xl tabular-nums" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Message (optional)</span>
            <textarea value={counterMsg} onChange={(e) => setCounterMsg(e.target.value)} rows={2} maxLength={500}
              data-testid="counter-message-input"
              className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm resize-none" />
          </label>
          <div className="flex gap-2 pt-1">
            <button onClick={() => setCounterFor(null)} disabled={counterBusy} data-testid="counter-cancel-btn"
              className="flex-1 border border-sniper hover:border-foreground text-secondary-foreground hover:text-foreground font-mono uppercase text-sm py-2.5 transition-colors">Cancel</button>
            <button onClick={submitCounter} disabled={counterBusy} data-testid="counter-submit-btn"
              className="flex-1 flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase py-2.5 transition-colors">
              {counterBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send counter"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
